/* eslint-disable jsx-a11y/anchor-is-valid */
import {useState, useEffect} from 'react'
import * as Yup from 'yup'
import clsx from 'clsx'

import {useFormik} from 'formik'
import {getUserByToken, login} from '../core/_requests'
import {useAuth} from '../core/Auth'
import {clearAllToasts} from '../../../utils/toast/Toast'
import axios from 'axios'
import {API_URL} from '../../../config/api.config'
import {UpdateConfirmationDialog} from './UpdateConfirmationDialog'
import {UpdateProgressDialog} from './UpdateProgressDialog'
import {NetworkStatusIndicator} from '../../../pages/wifi-settings/NetworkStatusIndicator'
import {WifiSettingsModal} from '../../../pages/wifi-settings/WifiSettingsModal'
import {usePowerActions} from '../../../hooks/usePowerActions'

const loginSchema = Yup.object().shape({
    username: Yup.string()
        .min(3, 'Minimum 3 symbols')
        .max(50, 'Maximum 50 symbols')
        .required('User Name is required'),
    password: Yup.string()
        .min(3, 'Minimum 3 symbols')
        .max(50, 'Maximum 50 symbols')
        .required('Password is required'),
});

const initialValues = {
    username: '',
    password: '',
};

const rotatingContent = [
    {
        icon: 'ki-cloud-download',
        title: 'Works Offline, Syncs Online',
        description:
            'Continue operations without internet — all data syncs automatically when connectivity is restored',
        color: 'primary',
    },
    {
        icon: 'ki-shield-tick',
        title: 'Zero Data Loss Guarantee',
        description:
            'Every sale, purchase, and weighbridge reading is safely stored locally and synced to the cloud',
        color: 'success',
    },
    {
        icon: 'ki-arrows-loop',
        title: 'Seamless Two-Way Sync',
        description:
            'Changes from the server and other clients are automatically merged — stay up to date across all devices',
        color: 'warning',
    },
    {
        icon: 'ki-technology-4',
        title: 'Weighbridge & Camera Integration',
        description:
            'Direct serial port weighbridge connectivity and live camera capture at the edge',
        color: 'info',
    },
    {
        icon: 'ki-rocket',
        title: 'Built for Remote Sites',
        description:
            'Designed for quarry sites with unreliable networks — fast, lightweight, and always available',
        color: 'danger',
    },
];

export function Login() {
    const [loading, setLoading] = useState(false);
    const [currentContentIndex, setCurrentContentIndex] = useState(0);
    const [isTransitioning, setIsTransitioning] = useState(false);
    const [updateAvailable, setUpdateAvailable] = useState(false);
    const [currentVersion, setCurrentVersion] = useState('');
    const [latestVersion, setLatestVersion] = useState('');
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [showUpdateDialog, setShowUpdateDialog] = useState(false);
    const [showWifiModal, setShowWifiModal] = useState(false);
    const [hideWifi, setHideWifi] = useState(false);
    const [hidePowerButtons, setHidePowerButtons] = useState(false);
    const {saveAuth, setCurrentUser} = useAuth();
    const power = usePowerActions();

    useEffect(() => {
        const interval = setInterval(() => {
            setIsTransitioning(true);
            setTimeout(() => {
                setCurrentContentIndex((prev) => (prev + 1) % rotatingContent.length);
                setIsTransitioning(false);
            }, 300);
        }, 5000);

        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        axios.get(`${API_URL}/api/open/version-check`)
            .then(({data}) => {
                setCurrentVersion(data.currentVersion || '');
                setLatestVersion(data.latestVersion || '');
                setUpdateAvailable(data.updateAvailable === true);
            })
            .catch(() => {});
    }, []);

    useEffect(() => {
        axios.get(`${API_URL}/api/open/login-config`)
            .then(({data}) => {
                setHideWifi(data.hideWifiOnLoginScreen === true);
                setHidePowerButtons(data.hidePowerButtonsOnLoginScreen === true);
            })
            .catch(() => {});
    }, []);

    const formik = useFormik({
        initialValues,
        validationSchema: loginSchema,
        onSubmit: async (values, {setStatus, setSubmitting}) => {
            setLoading(true);
            clearAllToasts();
            try {
                const {data: auth} = await login(values.username, values.password);
                clearAllToasts();
                saveAuth(auth);
                const {data: user} = await getUserByToken(auth.accessToken);
                setCurrentUser(user)
            } catch (error: any) {
                console.error(error);
                saveAuth(undefined);
                const message = error?.response?.data?.message || error?.response?.data || 'The login details are incorrect';
                setStatus(message);
                setSubmitting(false);
                setLoading(false)
            }
        },
    });

    return (
        <form
            className='form w-100'
            onSubmit={formik.handleSubmit}
            noValidate
            id='kt_login_signin_form'
        >
            {/* begin::Heading */}
            <div className='text-center mb-11'>
                <h1 className='mb-2' style={{
                    fontSize: '2.2rem',
                    fontWeight: 800,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase' as const,
                }}>
                    <span className='text-primary'>Rev</span>
                    <span className='text-dark'>Con</span>
                    <span className='text-success ms-2' style={{fontWeight: 300, letterSpacing: '0.15em'}}>Outpost</span>
                </h1>
                <div className='text-success fw-bold fs-7 mb-4' style={{letterSpacing: '0.25em', textTransform: 'uppercase' as const}}>
                    On-Site Operations Console
                </div>
                <div className='text-gray-600 fw-semibold fs-7'>
                    Offline-first quarry management — always operational, always in sync
                </div>
            </div>
            {/* end::Heading */}

            {!hideWifi && <NetworkStatusIndicator onConfigureClick={() => setShowWifiModal(true)} />}

            {formik.status ? (
                <div className='mb-lg-15 alert alert-danger'>
                    <div className='alert-text font-weight-bold'>{formik.status}</div>
                </div>
            ) : (
                <div
                    className={`mb-10 bg-light-${
                        rotatingContent[currentContentIndex].color
                    } p-6 rounded-3 border border-${
                        rotatingContent[currentContentIndex].color
                    } border-dashed transition-all duration-600 ${
                        isTransitioning
                            ? 'opacity-50 transform scale-95'
                            : 'opacity-100 transform scale-100'
                    }`}
                    style={{
                        transition: 'all 0.6s cubic-bezier(0.4, 0.0, 0.2, 1)',
                        minHeight: '120px',
                    }}
                >
                    <div className='d-flex align-items-center h-100'>
                        <div className='symbol symbol-40px me-3'>
                            <div
                                className={`symbol-label bg-${rotatingContent[currentContentIndex].color}`}
                            >
                                <i
                                    className={`ki-duotone ${rotatingContent[currentContentIndex].icon} fs-2 text-white`}
                                >
                                    <span className='path1'></span>
                                    <span className='path2'></span>
                                    <span className='path3'></span>
                                </i>
                            </div>
                        </div>
                        <div className='flex-grow-1'>
                            <div
                                className={`text-${rotatingContent[currentContentIndex].color} fw-bold fs-6 mb-1`}
                            >
                                {rotatingContent[currentContentIndex].title}
                            </div>
                            <div className='text-gray-700 fs-7'>
                                {rotatingContent[currentContentIndex].description}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* begin::Form group */}
            <div className='fv-row mb-8'>
                <label className='form-label fs-6 fw-bolder text-dark'>User Name</label>
                <input
                    placeholder=''
                    {...formik.getFieldProps('username')}
                    className={clsx(
                        'form-control bg-transparent',
                        {'is-invalid': formik.touched.username && formik.errors.username},
                        {
                            'is-valid': formik.touched.username && !formik.errors.username,
                        }
                    )}
                    type='username'
                    name='username'
                    autoComplete='off'
                />
                {formik.touched.username && formik.errors.username && (
                    <div className='fv-plugins-message-container'>
                        <span role='alert'>{formik.errors.username}</span>
                    </div>
                )}
            </div>
            {/* end::Form group */}

            {/* begin::Form group */}
            <div className='fv-row mb-3'>
                <label className='form-label fw-bolder text-dark fs-6 mb-0'>Password</label>
                <input
                    type='password'
                    autoComplete='new-password'
                    {...formik.getFieldProps('password')}
                    className={clsx(
                        'form-control bg-transparent',
                        {
                            'is-invalid': formik.touched.password && formik.errors.password,
                        },
                        {
                            'is-valid': formik.touched.password && !formik.errors.password,
                        }
                    )}
                />
                {formik.touched.password && formik.errors.password && (
                    <div className='fv-plugins-message-container'>
                        <div className='fv-help-block'>
                            <span role='alert'>{formik.errors.password}</span>
                        </div>
                    </div>
                )}
            </div>
            {/* end::Form group */}

            {/* begin::Action */}
            <div className='d-grid mb-10'>
                <button
                    type='submit'
                    id='kt_sign_in_submit'
                    className='btn btn-primary'
                    disabled={formik.isSubmitting || !formik.isValid}
                >
                    {!loading && <span className='indicator-label'>Continue</span>}
                    {loading && (
                        <span className='indicator-progress' style={{display: 'block'}}>
              Please wait...
              <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
            </span>
                    )}
                </button>
            </div>
            {/* end::Action */}

            {updateAvailable && (
                <div className='mb-8 bg-light-warning p-5 rounded-3 border border-warning border-dashed'>
                    <div className='d-flex align-items-center'>
                        <div className='symbol symbol-40px me-3'>
                            <div className='symbol-label bg-warning'>
                                <i className='ki-duotone ki-arrow-up fs-2 text-white'>
                                    <span className='path1'></span>
                                    <span className='path2'></span>
                                </i>
                            </div>
                        </div>
                        <div className='flex-grow-1'>
                            <div className='text-warning fw-bold fs-6 mb-1'>
                                Update Available
                            </div>
                            <div className='text-gray-700 fs-7'>
                                A new version <strong>{latestVersion}</strong> is available.
                                You are currently on <strong>{currentVersion}</strong>.
                            </div>
                        </div>
                    </div>
                    <button
                        type='button'
                        className='btn btn-sm btn-warning mt-3 w-100'
                        onClick={() => setShowConfirmDialog(true)}
                    >
                        <i className='ki-duotone ki-arrow-up fs-4 me-1'>
                            <span className='path1'></span>
                            <span className='path2'></span>
                        </i>
                        Update Now
                    </button>
                </div>
            )}

            <div className='bg-light-success p-4 rounded-3 mb-6'>
                <div className='d-flex align-items-center'>
                    <div className='symbol symbol-30px me-3'>
                        <div className='symbol-label bg-success'>
                            <i className='ki-duotone ki-cloud-download fs-5 text-white'>
                                <span className='path1'></span>
                                <span className='path2'></span>
                            </i>
                        </div>
                    </div>
                    <div className='flex-grow-1'>
                        <div className='text-gray-800 fw-bold fs-7'>
                            This is an offline-first edge client. Data syncs automatically with the server when online.
                        </div>
                    </div>
                </div>
            </div>

            <div className='text-gray-500 text-center fw-semibold fs-6'>
                <i className='ki-duotone ki-rocket fs-5 me-2'>
                    <span className='path1'></span>
                    <span className='path2'></span>
                </i>
                Transform your crushing plant operations?{' '}
                <a href='mailto:info@revcontech.com' className='link-primary text-hover-primary'>
                    Contact RevCon Tech
                </a>
            </div>

            {/* begin::Host power controls */}
            {!hidePowerButtons && (
                <>
                    <div className='separator separator-dashed my-6'></div>
                    <div className='d-flex justify-content-center gap-3 mb-2'>
                        <button
                            type='button'
                            className='btn btn-sm btn-light-warning'
                            disabled={power.busy}
                            onClick={power.requestRestart}
                        >
                            <i className='ki-duotone ki-arrows-circle fs-4 me-1'>
                                <span className='path1'></span>
                                <span className='path2'></span>
                            </i>
                            Restart Device
                        </button>
                        <button
                            type='button'
                            className='btn btn-sm btn-light-danger'
                            disabled={power.busy}
                            onClick={power.requestShutdown}
                        >
                            <i className='ki-duotone ki-entrance-right fs-4 me-1'>
                                <span className='path1'></span>
                                <span className='path2'></span>
                            </i>
                            Shutdown Device
                        </button>
                    </div>
                    <div className='text-center text-muted fs-8'>
                        Cleanly powers off or reboots this Raspberry Pi
                    </div>
                </>
            )}
            {/* end::Host power controls */}

            {power.dialogs}

            <UpdateConfirmationDialog
                show={showConfirmDialog}
                currentVersion={currentVersion}
                latestVersion={latestVersion}
                onCancel={() => setShowConfirmDialog(false)}
                onConfirm={() => {
                    setShowConfirmDialog(false)
                    setShowUpdateDialog(true)
                }}
            />

            <UpdateProgressDialog
                show={showUpdateDialog}
                onComplete={() => setShowUpdateDialog(false)}
            />

            {!hideWifi && (
                <WifiSettingsModal
                    show={showWifiModal}
                    onHide={() => setShowWifiModal(false)}
                />
            )}
        </form>
    )
}
